import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CreditCard, Loader2, CheckCircle2, AlertCircle, RefreshCw, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';

// Org-level Stripe (Standard Connect) connection so the org can collect client
// payments directly into their own Stripe account. Owner/admin only.
export const PaymentsSettings = () => {
  const { api } = useAuth();
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);
  const [account, setAccount] = useState(null);
  const [connectReady, setConnectReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadAccount = async (orgId) => {
    const r = await api.get(`/payment-accounts?organization_id=${orgId}`);
    setAccount(r.data.account);
    setConnectReady(r.data.connect_ready);
  };

  const init = async () => {
    setLoading(true);
    try {
      const r = await api.get('/orgs');
      const orgs = Array.isArray(r.data) ? r.data : (r.data.organizations || r.data.orgs || []);
      const mine = orgs.find((o) => ['owner', 'admin'].includes(o.role)) || orgs[0];
      if (!mine) { setLoading(false); return; }
      setOrg(mine); setRole(mine.role);
      await loadAccount(mine.id);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    init();
    const p = new URLSearchParams(window.location.search);
    if (p.get('connect') === 'success') toast.success('Stripe account connected');
    else if (p.get('connect') === 'error') toast.error('Stripe connection was cancelled or failed');
    if (p.get('connect')) window.history.replaceState({}, '', '/settings');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payment-accounts/stripe/connect-url?organization_id=${org.id}`);
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Stripe connection'); setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post(`/payment-accounts/stripe/refresh?organization_id=${org.id}`); setAccount(r.data.account); toast.success('Status refreshed'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not refresh'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!account?.id) return;
    setBusy(true);
    try { await api.delete(`/payment-accounts/${account.id}`); setAccount(null); toast.success('Stripe account disconnected'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not disconnect'); }
    finally { setBusy(false); }
  };

  const canManage = ['owner', 'admin'].includes(role);

  return (
    <Card className="border-zinc-200" data-testid="payments-settings">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-indigo-600" /> Client Payments</CardTitle>
        <CardDescription>Connect your own Stripe account to collect payments from your clients — funds go directly to you.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        ) : !org ? (
          <p className="text-sm text-zinc-400">You need an organization to connect payments.</p>
        ) : account ? (
          <div className="space-y-3" data-testid="stripe-connected">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-zinc-800 font-medium">Stripe connected</span>
              <code className="text-[11px] text-zinc-400">{account.stripe_account_id}</code>
            </div>
            <div className="flex items-center gap-2 text-[12px]">
              {account.charges_enabled
                ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ready to accept payments</span>
                : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Finish setup in Stripe to enable charges</span>}
              {!account.stripe_livemode && <span className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Test mode</span>}
            </div>
            {canManage && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="stripe-refresh-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Refresh status</Button>
                <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={disconnect} disabled={busy} data-testid="stripe-disconnect-btn"><Unlink className="w-4 h-4 mr-1.5" /> Disconnect</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-zinc-500">Link your existing Stripe account in one step. Client payments for your proposals will be charged directly on your account.</p>
            {!connectReady && <p className="text-[12px] text-amber-700 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Stripe Connect isn't enabled on the platform yet — contact support.</p>}
            {canManage ? (
              <Button onClick={connect} disabled={busy || !connectReady} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="connect-stripe-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Link2 className="w-4 h-4 mr-1.5" />} Connect Stripe
              </Button>
            ) : (
              <p className="text-[12px] text-zinc-400">Only an organization owner or admin can connect a payment account.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentsSettings;
